import { ScribbleType } from 'src/app/services/session-shared-data.service';
import { defaults } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { AppConfigurationService } from '../../iframe/additional-apps.utils';
import { YCanvasItemObject, YObject } from '../../../common/y-session';
import { BoardFreeSpaceFinder } from '../board-free-space-finder.service';
import { Position } from '../../../../services/items-canvas.service';
import { FlagsService } from '../../../../services/flags.service';
import { BoardItemsStaggeringService } from './board-items-staggering.service';

/**
 * Context for creating board items, used as fallback values in BoardItemConfig.*
 * Provides positioning (centerX/Y), ownership (userId, boardId), z-index ordering,
 * and required services for item creation and positioning.
 */
export interface CreationContext {
  centerXCoordinate: number;
  centerYCoordinate: number;
  // The newly added object should always be visible on top of other objects, this variable helps track the top-most object, so we can make the new one on top of it.
  maxZIndexOnTheBoard: number;
  currentUserId: string;
  currentBoardId: string;

  renderInNearestFreeSpace: boolean;
  spaceIdToRenderIn: string;

  // This means that the item have just been created by the current user, not by others or created before, and now we are just loading it.
  isNewlyCreatedItem: boolean;
  appConfigurationService: AppConfigurationService;
  boardItemsStaggeringService: BoardItemsStaggeringService;
  boardFreeSpaceFinder: BoardFreeSpaceFinder;
  featureFlagsService: FlagsService;
}

export const DEFAULT_MIN_WIDTH = -Infinity;
export const DEFAULT_MIN_HEIGHT = -Infinity;
export const DEFAULT_MAX_WIDTH = Infinity;
export const DEFAULT_MAX_HEIGHT = Infinity;

export const OTHER_ITEMS_DEFAULT_HEIGHT = 700;
export const OTHER_ITEMS_DEFAULT_WIDTH = 700;

/**
 * Core configuration class for board items that defines their properties and behavior.
 * This class serves as the data model for all board items, containing:
 * - Position and dimensions (top, left, width, height)
 * - Transform properties (angle, scale)
 * - Ownership and identification (spaceId, boardId, ownerId, uid)
 * - Constraints (minWidth, maxWidth, minHeight, maxHeight)
 * - State properties (locked, order)
 *
 * The class provides static factory methods to create configurations from various sources
 * and handles default value initialization.
 */
export class BoardItemConfig {
  spaceId: string;
  boardId: string;
  top: number;
  left: number;
  angle: number;
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
  ownerId: string;
  order: number;
  locked: boolean;
  minWidth: number;
  maxWidth: number;
  minHeight: number;
  maxHeight: number;

  // Helps determine the version of the object, and is used to determine either to update the configs or not
  vuid?: string;
  uid: string;
  ScribbleType: ScribbleType;

  constructor(
    boardItemConfigurations: Partial<BoardItemConfig>,
    {
      centerXCoordinate,
      centerYCoordinate,
      maxZIndexOnTheBoard,
      currentUserId,
      spaceIdToRenderIn,
      currentBoardId,
      renderInNearestFreeSpace,
      boardItemsStaggeringService,
      boardFreeSpaceFinder,
      isNewlyCreatedItem,
    }: CreationContext,
  ) {
    const defaultConfigurations = {
      angle: 0,
      zIndex: -1,
      width: OTHER_ITEMS_DEFAULT_WIDTH,
      height: OTHER_ITEMS_DEFAULT_HEIGHT,
      scaleX: 1,
      scaleY: 1,
      locked: false,
      ScribbleType: ScribbleType.NOT_SCRIBBLE,
      minWidth: DEFAULT_MIN_WIDTH,
      maxWidth: DEFAULT_MAX_WIDTH,
      minHeight: DEFAULT_MIN_HEIGHT,
      maxHeight: DEFAULT_MAX_HEIGHT,
      uid: uuidv4(),
    };

    const initialConfigurations = {
      top: centerYCoordinate - (boardItemConfigurations.height ?? defaultConfigurations.height) / 2,
      left: centerXCoordinate - (boardItemConfigurations.width ?? defaultConfigurations.width) / 2,
      spaceId: spaceIdToRenderIn,
      boardId: currentBoardId,
      ownerId: currentUserId,
      order: maxZIndexOnTheBoard,
    };

    if (isNewlyCreatedItem) {
      const position = this.getFinalInsertionPosition(
        renderInNearestFreeSpace,
        boardFreeSpaceFinder,
        boardItemConfigurations,
        defaultConfigurations,
        initialConfigurations,
        boardItemsStaggeringService,
      );
      boardItemConfigurations.top = position.top;
      boardItemConfigurations.left = position.left;
    }

    const configurations = defaults(boardItemConfigurations, {
      ...initialConfigurations,
      ...defaultConfigurations,
    });

    this.spaceId = configurations.spaceId;
    this.boardId = configurations.boardId;
    this.top = configurations.top;
    this.left = configurations.left;
    this.angle = configurations.angle;
    this.width = configurations.width;
    this.height = configurations.height;
    this.scaleX = configurations.scaleX;
    this.scaleY = configurations.scaleY;
    this.ownerId = configurations.ownerId;
    this.order = configurations.order;
    this.locked = configurations.locked;
    this.minWidth = configurations.minWidth;
    this.maxWidth = configurations.maxWidth;
    this.minHeight = configurations.minHeight;
    this.maxHeight = configurations.maxHeight;

    this.vuid = configurations.vuid;
    this.uid = configurations.uid;
    this.ScribbleType = configurations.ScribbleType;
  }

  private getFinalInsertionPosition(
    renderInNearestFreeSpace: boolean,
    boardFreeSpaceFinder: BoardFreeSpaceFinder,
    boardItemConfigurations: Partial<BoardItemConfig>,
    defaultConfigurations: {
      width: number;
      height: number;
    },
    initialConfigurations: {
      top: number;
      left: number;
      boardId: string;
    },
    boardItemsStaggeringService: BoardItemsStaggeringService,
  ) {
    let newPosition: Position;
    if (renderInNearestFreeSpace) {
      newPosition = boardFreeSpaceFinder.getPlacementCoordsOnCanvas(
        boardItemConfigurations.width ?? defaultConfigurations.width,
        boardItemConfigurations.height ?? defaultConfigurations.height,
        boardItemConfigurations.boardId ?? initialConfigurations.boardId,
      );
    } else {
      newPosition = boardItemsStaggeringService.getStaggeredPosition(
        {
          top: boardItemConfigurations.top ?? initialConfigurations.top,
          left: boardItemConfigurations.left ?? initialConfigurations.left,
        },
        boardItemConfigurations.boardId ?? initialConfigurations.boardId,
      );
    }
    return newPosition;
  }

  static fromYCanvasItemObject(
    yCanvasItemObject: YCanvasItemObject,
    globalCreationParams: CreationContext,
    defaultConfigurations: Partial<BoardItemConfig> = {},
  ): BoardItemConfig {
    return new BoardItemConfig(
      {
        ...defaultConfigurations,
        ownerId: yCanvasItemObject.ownerId ?? yCanvasItemObject.userId,
        locked: yCanvasItemObject.locked,

        top: +(yCanvasItemObject.position?.get('top') ?? defaultConfigurations.top ?? 0),
        left: +(yCanvasItemObject.position?.get('left') ?? defaultConfigurations.left ?? 0),
        width: +(yCanvasItemObject.position?.get('width') ?? defaultConfigurations.width ?? 0),
        height: +(yCanvasItemObject.position?.get('height') ?? defaultConfigurations.height ?? 0),
        scaleX: +(yCanvasItemObject.position?.get('scaleX') ?? defaultConfigurations.scaleX ?? 1),
        scaleY: +(yCanvasItemObject.position?.get('scaleY') ?? defaultConfigurations.scaleY ?? 1),
        order: +(yCanvasItemObject.position?.get('index') ?? defaultConfigurations.order ?? 0),
        angle: +(yCanvasItemObject.position?.get('angle') ?? defaultConfigurations.angle ?? 0),
        vuid: yCanvasItemObject.vuid,
        uid: yCanvasItemObject.uid,
        ScribbleType: ScribbleType.NOT_SCRIBBLE,
      },
      globalCreationParams,
    );
  }

  static fromYObject(
    object: YObject,
    globalCreationParams: CreationContext,
    defaultConfigurations: Partial<BoardItemConfig> = {},
  ) {
    return new BoardItemConfig(
      {
        ...defaultConfigurations,
        ownerId: object.userId,
        locked: object['locked'],

        top: object.top,
        left: object.left,
        width: object.width,
        height: object.height,
        scaleX: object.scaleX,
        scaleY: object.scaleY,
        order: object['order'],
        angle: object.angle,

        vuid: object.vuid,
        uid: object.uid,
        ScribbleType: ScribbleType.NOT_SCRIBBLE,
      },
      globalCreationParams,
    );
  }
}
